import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import Layouts from "../../layouts/layouts";
import DataTable_Component from "../../components/profitandloss/Data Table/index";
import * as XLSX from "xlsx";

export default function ProfitandLoss() {
  const title = "Profit & Loss";

  const [apiData, setApiData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
  });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [summary, setSummary] = useState({
    trips: 0,
    revenue: 0,
    lease: 0,
    totalExpense: 0,
    directExpense: 0,
    indirectExpense: 0,
    profit: 0,
    loss: 0,
    netPL: 0,
  });

  // const selectedMonth = moment().month() + 1;
  // const selectedYear = moment().year();

  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
  const [selectedYear, setSelectedYear] = useState(moment().year());

  /* ✅ SINGLE SOURCE OF TRUTH */
  const fetchData = async (
    page = 1,
    month = selectedMonth,
    year = selectedYear,
    search = "",
    limit = pagination.limit,
    from = fromDate,
    to = toDate,
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/pnl/monthly`,
        {
          params: {
            page,
            limit,
            search,
            ...(from && to
              ? {
                  fromDate: from,
                  toDate: to,
                }
              : {
                  month,
                  year,
                }),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.data.success) return;

      const summary = response.data.summary || {};

      setSummary(summary);
      setSelectedMonth(month);
      setSelectedYear(year);
      setApiData(response.data.dateSummary || []);

      setPagination(response.data.pagination || {});

      const columns = [
        {
          name: <b>Date</b>,
          selector: (row) => moment(row.date).format("DD MMM YYYY"),
          sortable: true,
          minWidth: "150px",
        },
        {
          name: <b>Trips ({summary.trips})</b>,
          selector: (row) => row.trips,
          sortable: true,
          center: true,
        },
        {
          name: <b>Revenue ₹{Number(summary.revenue || 0).toLocaleString()}</b>,
          selector: (row) =>
            Number(row.revenue).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
        {
          name: <b>Lease ₹{Number(summary.lease || 0).toLocaleString()}</b>,
          selector: (row) =>
            Number(row.lease).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
        {
          name: (
            <b>
              Total Expense ₹
              {Number(summary.totalExpense || 0).toLocaleString()}
            </b>
          ),
          selector: (row) =>
            Number(row.totalExpense).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
        {
          name: (
            <b>
              Direct Expense ₹{Number(summary.directExpense).toLocaleString()}
            </b>
          ),
          selector: (row) =>
            Number(row.directExpense).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
        {
          name: (
            <b>
              Indirect Expense ₹
              {Number(summary.indirectExpense).toLocaleString()}
            </b>
          ),
          selector: (row) =>
            Number(row.indirectExpense).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
        {
          name: (
            <div
              style={{
                background: "#d4edda",
                color: "#155724",
                padding: "6px 10px",
                borderRadius: "4px",
                fontWeight: "bold",
                textAlign: "center",
                width: "100%",
              }}
            >
              Profit ₹{Number(summary.profit || 0).toLocaleString()}
            </div>
          ),
          selector: (row) =>
            Number(row.profit).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
        {
          name: (
            <div
              style={{
                background: "#f8d7da",
                color: "#721c24",
                padding: "6px 10px",
                borderRadius: "4px",
                fontWeight: "bold",
                textAlign: "center",
                width: "100%",
              }}
            >
              Loss ₹{Number(summary.loss || 0).toLocaleString()}
            </div>
          ),
          selector: (row) =>
            Number(row.loss).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          sortable: true,
          center: true,
        },
      ];

      setColumns(columns);
    } catch (error) {
      console.error(error);
    }
  };

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/pnl/monthly`,
        {
          params: {
            // ❌ Don't send page & limit
            ...(fromDate && toDate
              ? {
                  fromDate,
                  toDate,
                }
              : {
                  month: selectedMonth,
                  year: selectedYear,
                }),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.data.success) return;

      const excelData = (response.data.dateSummary || []).map((item) => ({
        Date: moment(item.date).format("DD-MM-YYYY"),
        Trips: item.trips,
        Revenue: item.revenue,
        Lease: item.lease,
        "Total Expense": item.totalExpense,
        "Direct Expense": item.directExpense,
        "Indirect Expense": item.indirectExpense,
        Profit: item.profit,
        Loss: item.loss,
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Profit & Loss");

      XLSX.writeFile(workbook, `Profit_Loss.xlsx`);
    } catch (error) {
      console.error("Excel Export Error:", error);
    }
  };

  /* ✅ INITIAL LOAD */
  useEffect(() => {
    fetchData(1);
  }, []);

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              {/* <div className="card-header">
                <h4>{title}</h4>
              </div> */}
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4>{title}</h4>

                <button
                  className="btn btn-success btn-sm"
                  onClick={exportToExcel}
                >
                  Export to Excel
                </button>
              </div>

              <div className="card-body">
                <DataTable_Component
                  columns={columns}
                  fetchData={fetchData}
                  summary={summary}
                  paginationData={{
                    data: apiData,
                    page: pagination.page,
                    limit: pagination.limit,
                    totalClients: pagination.totalRecords,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
